import { useEffect, useMemo, useState } from 'react';
import type { CompassLocale } from './i18n';
import {
  COMPASS_LOCALE_STORAGE_KEY,
  detectCompassLocale,
  getCompassCopy,
} from './i18n';
import { useCatalog } from './hooks/useCatalog';
import { readStoredLocale } from './components/LocaleSwitch';
import { PreferenceControls } from './components/PreferenceControls';
import { Gallery } from './components/Gallery';
import { Detail } from './components/Detail';
import { AppsGallery } from './components/AppsGallery';
import { PromptLibrary } from './components/PromptLibrary';
import { DemosView } from './components/DemosView';
import { AboutView } from './components/AboutView';
import {
  buildCompassViewHref,
  normalizeCompassView,
  type CompassView,
} from './lib/catalogPresentation';

function routeAppId(pathname: string): string | undefined {
  const match = pathname.match(/^\/apps\/([^/]+)\/?$/);
  return match?.[1];
}

function routeView(search: string): CompassView {
  return normalizeCompassView(new URLSearchParams(search).get('view'));
}

export function App(): React.JSX.Element {
  const { catalog, error } = useCatalog();
  const [locale, setLocale] = useState<CompassLocale>(() => detectCompassLocale({
    queryLang: new URLSearchParams(window.location.search).get('lang'),
    storedLang: readStoredLocale(),
    navigatorLanguages: navigator.languages,
  }));
  const appId = useMemo(() => routeAppId(window.location.pathname), []);
  const view = useMemo(() => routeView(window.location.search), []);
  const copy = useMemo(() => getCompassCopy(locale), [locale]);
  const activeView = appId ? 'gallery' : view;

  useEffect(() => {
    document.documentElement.lang = locale;
    try {
      window.localStorage.setItem(COMPASS_LOCALE_STORAGE_KEY, locale);
    } catch {
      // Ignore storage failures and keep runtime state only.
    }
    const url = new URL(window.location.href);
    if (url.searchParams.get('lang') !== locale) {
      url.searchParams.set('lang', locale);
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, [locale]);

  const navItems: Array<{ key: CompassView; label: string }> = [
    { key: 'gallery', label: 'Gallery' },
    { key: 'library', label: 'Library' },
    { key: 'demos', label: 'Demos' },
    { key: 'about', label: 'About' },
  ];

  const nav = (
    <nav className="glass-panel-l2 fixed top-0 w-full z-50 transition-all duration-300">
      <div className="flex justify-between items-center px-4 md:px-10 max-w-[1280px] mx-auto min-h-20 gap-6">
        <a href={buildCompassViewHref('home', locale)} className="font-geist text-[26px] text-on-surface no-underline font-bold shrink-0">
          Compass Gallery
        </a>
        <div className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => {
            const active = activeView === item.key;
            return (
              <a
                key={item.key}
                className={active
                  ? 'text-primary border-b-2 border-primary pb-1 no-underline font-geist font-semibold text-sm'
                  : 'text-on-surface-variant hover:text-on-surface transition-all hover:scale-105 no-underline font-geist font-semibold text-sm'}
                href={buildCompassViewHref(item.key, locale)}
              >
                {item.label}
              </a>
            );
          })}
        </div>
        <div className="flex items-center gap-4 md:gap-8">
          <PreferenceControls locale={locale} copy={copy} onLocaleChange={setLocale} />
          <a className="btn-primary-gradient px-6 py-2.5 rounded-full hover:scale-105 transition-transform duration-200 text-sm no-underline" href={buildCompassViewHref('gallery', locale)}>
            Get Started
          </a>
        </div>
      </div>
    </nav>
  );

  const footer = (
    <footer className="bg-surface-container-lowest text-on-surface-variant py-16 border-t border-outline-variant/10 mt-auto">
      <div className="max-w-[1280px] mx-auto px-4 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="flex flex-col gap-6">
          <div className="font-geist text-3xl font-bold text-on-surface">Compass Gallery</div>
          <p className="text-sm max-w-sm leading-relaxed">© 2026 Compass Gallery AI. Built for the future of generative interfaces.</p>
        </div>
        <div className="flex flex-wrap gap-8 items-center md:justify-end">
          <a className="text-on-surface-variant hover:text-primary transition-colors no-underline text-sm font-semibold" href={buildCompassViewHref('gallery', locale)}>Gallery</a>
          <a className="text-on-surface-variant hover:text-primary transition-colors no-underline text-sm font-semibold" href={buildCompassViewHref('library', locale)}>Library</a>
          <a className="text-on-surface-variant hover:text-primary transition-colors no-underline text-sm font-semibold" href={buildCompassViewHref('demos', locale)}>Demos</a>
          <a className="text-on-surface-variant hover:text-primary transition-colors no-underline text-sm font-semibold" href={buildCompassViewHref('about', locale)}>About</a>
        </div>
      </div>
    </footer>
  );

  let content: React.ReactNode;

  if (error) {
    content = (
      <main className="flex-grow pt-32 pb-20 px-6 max-w-[1280px] mx-auto w-full">
        <div className="glass-panel-l2 rounded-3xl p-16 text-center flex flex-col items-center gap-8 animate-fade-in">
          <span className="material-symbols-outlined text-7xl text-error">error</span>
          <h2 className="font-geist text-4xl text-on-surface">System Error</h2>
          <p className="text-xl text-on-surface-variant max-w-xl">{error}</p>
        </div>
      </main>
    );
  } else if (!catalog) {
    content = (
      <main className="flex-grow pt-32 pb-20 px-6 max-w-[1280px] mx-auto w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-10 animate-fade-in">
           <div className="w-20 h-20 border-[6px] border-primary/20 border-t-primary rounded-full animate-spin"></div>
           <p className="font-geist text-2xl text-on-surface-variant font-medium">{copy.loadingCatalog}</p>
        </div>
      </main>
    );
  } else if (appId) {
    const app = catalog.apps.find((item) => item.appId === appId);
    if (!app) {
      content = (
        <main className="flex-grow pt-32 pb-20 px-6 max-w-[1280px] mx-auto w-full">
          <div className="glass-panel-l2 rounded-3xl p-16 text-center flex flex-col items-center gap-8 animate-fade-in">
            <span className="material-symbols-outlined text-7xl text-outline">search_off</span>
            <h2 className="font-geist text-4xl text-on-surface">{copy.appNotFound}</h2>
            <a href={buildCompassViewHref('gallery', locale)} className="btn-secondary-cyan px-10 py-3 rounded-full no-underline">Back to Gallery</a>
          </div>
        </main>
      );
    } else {
      content = <div className="animate-fade-in"><Detail app={app} apps={catalog.apps} locale={locale} copy={copy} onLocaleChange={setLocale} isIntegrated /></div>;
    }
  } else {
    if (view === 'gallery') {
      content = <div className="animate-fade-in"><AppsGallery apps={catalog.apps} locale={locale} /></div>;
    } else if (view === 'library') {
      content = <div className="animate-fade-in"><PromptLibrary apps={catalog.apps} locale={locale} /></div>;
    } else if (view === 'demos') {
      content = <div className="animate-fade-in"><DemosView apps={catalog.apps} locale={locale} /></div>;
    } else if (view === 'about') {
      content = <div className="animate-fade-in"><AboutView apps={catalog.apps} locale={locale} /></div>;
    } else {
      content = <div className="animate-fade-in"><Gallery apps={catalog.apps} locale={locale} copy={copy} onLocaleChange={setLocale} isIntegrated /></div>;
    }
  }

  return (
    <div className="bg-background text-on-surface min-h-screen flex flex-col selection:bg-primary/30 selection:text-white">
      {nav}
      {content}
      {footer}
    </div>
  );
}
